import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Modal, Form, InputGroup, Badge, Nav, Tab, Alert } from 'react-bootstrap';
import { fetchBooks, createBook, updateBook, deleteBook, rentBook, returnBook, fetchRentalHistory, bulkCreateBooks, getActiveRentalsByUser } from '../../services/bookService';
import { getUser } from '../../services/authService';

// Türkçe karakterleri normalize eden fonksiyon (arama için)
const normalizeTurkish = (text) => {
    if (!text) return '';
    // ÖNEMLİ: Önce Türkçe karakterleri değiştir, SONRA toLowerCase yap!
    // Aksi halde JavaScript'in toLowerCase()'u Türkçe karakterleri bozar
    return String(text)
        .replace(/İ/g, 'i')
        .replace(/I/g, 'i')
        .replace(/ı/g, 'i')
        .replace(/Ğ/g, 'g')
        .replace(/ğ/g, 'g')
        .replace(/Ü/g, 'u')
        .replace(/ü/g, 'u')
        .replace(/Ş/g, 's')
        .replace(/ş/g, 's')
        .replace(/Ö/g, 'o')
        .replace(/ö/g, 'o')
        .replace(/Ç/g, 'c')
        .replace(/ç/g, 'c')
        .toLowerCase();
};

const BookManagement = () => {
    const [activeTab, setActiveTab] = useState('books');
    const [allBooks, setAllBooks] = useState([]); // Tüm kitaplar (filtrelenmemiş)
    const [books, setBooks] = useState([]); // Görüntülenecek kitaplar (filtrelenmiş ve sayfalanmış)
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [renting, setRenting] = useState(false);
    const [returning, setReturning] = useState(null);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkLoading, setBulkLoading] = useState(false);
    const fileInputRef = useRef(null);
    const [pagination, setPagination] = useState({ page: 1, pageSize: 50, totalCount: 0 });
    const [historyPagination, setHistoryPagination] = useState({ page: 1, pageSize: 10, totalCount: 0 });
    const [searchTerm, setSearchTerm] = useState('');
    const [historySearchTerm, setHistorySearchTerm] = useState('');
    const [historyStatus, setHistoryStatus] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showRentModal, setShowRentModal] = useState(false);
    const [selectedBook, setSelectedBook] = useState(null);
    const [formData, setFormData] = useState({
        standNo: '',
        rafNo: '',
        kitapAdi: '',
        yazarAdi: '',
        yayinEvi: '',
        siraNo: 0
    });
    const [rentData, setRentData] = useState({
        adSoyad: '',
        tc: '',
        telefon: '',
        mail: '',
        durationDays: 15
    });
    const [activeRentalsWarning, setActiveRentalsWarning] = useState(null);
    const [checkingActiveRentals, setCheckingActiveRentals] = useState(false);
    const [showOnlyRented, setShowOnlyRented] = useState(false);

    const user = getUser();
    const role = (user?.userType || user?.role || '').toLowerCase();
    const canManage = role === 'admin' || role === 'library';
    const isAdmin = role === 'admin'; // Sadece admin için özel yetkiler

    // Tüm kitapları yükle (bir kerede)
    const loadAllBooks = useCallback(async () => {
        setLoading(true);
        try {
            // Tüm kitapları getir (sayfa limiti olmadan, büyük bir sayfa boyutu ile)
            const response = await fetchBooks({
                page: 1,
                pageSize: 10000, // Tüm kitapları getir
                searchTerm: '' // Arama yapma, tümünü getir
            });
            if (response?.isSuccess && response?.data) {
                const paginatedData = response.data;
                const items = paginatedData.items || [];
                setAllBooks(Array.isArray(items) ? items : []);
            } else {
                setAllBooks([]);
            }
        } catch (error) {
            console.error('Error fetching books:', error);
            setAllBooks([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Frontend'de filtreleme ve sayfalama
    useEffect(() => {
        let filtered = [...allBooks];

        // Sadece kiralanmış kitapları göster filtresi
        if (showOnlyRented) {
            filtered = filtered.filter(book => book.isRented === true);
        }

        // ARAMA ALGORİTMASI - SIFIRDAN
        if (searchTerm && searchTerm.trim() !== '') {
            // Arama terimini normalize et (Türkçe karakter desteği için)
            const search = normalizeTurkish(searchTerm.trim());
            
            filtered = filtered.filter(book => {
                // Her alanı string'e çevir ve normalize et (Türkçe karakter desteği)
                const kitapAdi = normalizeTurkish(String(book.kitapAdi || ''));
                const yazarAdi = normalizeTurkish(String(book.yazarAdi || ''));
                const yayinEvi = normalizeTurkish(String(book.yayinEvi || ''));
                const standNo = normalizeTurkish(String(book.standNo || ''));
                const rafNo = normalizeTurkish(String(book.rafNo || ''));
                const siraNo = normalizeTurkish(String(book.siraNo || ''));
                
                // Basit includes kontrolü - herhangi bir alanda geçiyorsa true döner
                // Örnek: "1" yazınca -> içinde "1" olan tüm kitaplar
                // "10" yazınca -> içinde "10" olan tüm kitaplar (10, 100, 105, 1000, A10, etc.)
                // "100" yazınca -> içinde "100" olan tüm kitaplar (100, 1000, 1007, A100, etc.)
                // Türkçe karakter: "martı" veya "marti" yazınca -> "MARTI" bulunur
                return (
                    kitapAdi.includes(search) ||
                    yazarAdi.includes(search) ||
                    yayinEvi.includes(search) ||
                    standNo.includes(search) ||
                    rafNo.includes(search) ||
                    siraNo.includes(search)
                );
            });
        }

        // Toplam sayıyı güncelle
        const totalCount = filtered.length;
        setPagination(prev => ({ ...prev, totalCount }));

        // Sayfalama uygula
        const startIndex = (pagination.page - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        const paginatedBooks = filtered.slice(startIndex, endIndex);

        setBooks(paginatedBooks);
    }, [allBooks, searchTerm, pagination.page, pagination.pageSize, showOnlyRented]);

    const loadHistory = useCallback(async () => {
        setHistoryLoading(true);
        try {
            const response = await fetchRentalHistory({
                page: historyPagination.page,
                pageSize: historyPagination.pageSize,
                searchTerm: historySearchTerm,
                status: historyStatus
            });
            if (response?.isSuccess && response?.data) {
                const paginatedData = response.data;
                const items = paginatedData.items || [];
                const totalCount = paginatedData.totalCount || 0;
                
                setHistory(Array.isArray(items) ? items : []);
                setHistoryPagination(prev => ({ ...prev, totalCount }));
            } else {
                setHistory([]);
            }
        } catch (error) {
            console.error('Error fetching rental history:', error);
            setHistory([]);
        } finally {
            setHistoryLoading(false);
        }
    }, [historyPagination.page, historyPagination.pageSize, historySearchTerm, historyStatus]);

    useEffect(() => {
        loadAllBooks();
    }, [loadAllBooks]);

    useEffect(() => {
        if (activeTab === 'history') {
            loadHistory();
        }
    }, [activeTab, loadHistory]);


    const handleAddEdit = async (e) => {
        e.preventDefault();
        try {
            if (selectedBook) {
                await updateBook({ ...formData, id: selectedBook.id });
            } else {
                await createBook(formData);
            }
            setShowAddModal(false);
            setFormData({ standNo: '', rafNo: '', kitapAdi: '', yazarAdi: '', yayinEvi: '', siraNo: 0 });
            setSelectedBook(null);
            loadAllBooks(); // Tüm kitapları yeniden yükle
        } catch (error) {
            console.error('Error saving book:', error);
            alert('İşlem sırasında bir hata oluştu.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Bu kitabı silmek istediğinize emin misiniz?')) {
            try {
                await deleteBook(id);
                loadAllBooks(); // Tüm kitapları yeniden yükle
            } catch (error) {
                console.error('Error deleting book:', error);
            }
        }
    };

    const handleRent = async (e) => {
        e.preventDefault();
        setRenting(true);
        try {
            await rentBook({ 
                id: selectedBook.id, 
                adSoyad: rentData.adSoyad,
                tc: rentData.tc,
                telefon: rentData.telefon,
                mail: rentData.mail,
                durationDays: rentData.durationDays
            });
            setShowRentModal(false);
            setRentData({ adSoyad: '', tc: '', telefon: '', mail: '', durationDays: 15 });
            setSelectedBook(null);
            loadAllBooks(); // Tüm kitapları yeniden yükle
            if (activeTab === 'history') {
                loadHistory();
            }
        } catch (error) {
            console.error('Error renting book:', error);
            alert(error.response?.data?.resultMessage || 'Kiralama işlemi başarısız.');
        } finally {
            setRenting(false);
        }
    };

    const handleReturn = async (id) => {
        if (window.confirm('Kitabın iade edildiğini onaylıyor musunuz?')) {
            setReturning(id);
            try {
                await returnBook(id);
                loadAllBooks(); // Tüm kitapları yeniden yükle
                if (activeTab === 'history') {
                    loadHistory();
                }
            } catch (error) {
                console.error('Error returning book:', error);
            } finally {
                setReturning(null);
            }
        }
    };

    const openAddModal = () => {
        setSelectedBook(null);
        setFormData({ standNo: '', rafNo: '', kitapAdi: '', yazarAdi: '', yayinEvi: '', siraNo: 0 });
        setShowAddModal(true);
    };

    const openEditModal = (book) => {
        setSelectedBook(book);
        setFormData({
            standNo: book.standNo,
            rafNo: book.rafNo,
            kitapAdi: book.kitapAdi,
            yazarAdi: book.yazarAdi,
            yayinEvi: book.yayinEvi,
            siraNo: book.siraNo
        });
        setShowAddModal(true);
    };

    const openRentModal = (book) => {
        setSelectedBook(book);
        setRentData({ adSoyad: '', tc: '', telefon: '', mail: '', durationDays: 15 });
        setRenting(false); // Modal açılırken loading state'ini sıfırla
        setActiveRentalsWarning(null); // Uyarıyı temizle
        setShowRentModal(true);
    };

    // Aktif kiralama kontrolü
    const checkActiveRentals = useCallback(async (tc, mail) => {
        if (!tc && !mail) {
            setActiveRentalsWarning(null);
            return;
        }

        setCheckingActiveRentals(true);
        try {
            const response = await getActiveRentalsByUser({ tc, mail });
            if (response?.isSuccess && response?.data && response.data.length > 0) {
                const activeCount = response.data.length;
                const bookNames = response.data.map(r => r.bookName || r.BookName || 'Bilinmeyen').join(', ');
                setActiveRentalsWarning({
                    count: activeCount,
                    bookNames: bookNames
                });
            } else {
                setActiveRentalsWarning(null);
            }
        } catch (error) {
            console.error('Error checking active rentals:', error);
            setActiveRentalsWarning(null);
        } finally {
            setCheckingActiveRentals(false);
        }
    }, []);

    // TC veya Mail değiştiğinde aktif kiralama kontrolü yap
    useEffect(() => {
        if (showRentModal && (rentData.tc || rentData.mail)) {
            const timeoutId = setTimeout(() => {
                checkActiveRentals(rentData.tc, rentData.mail);
            }, 500); // Debounce: 500ms bekle

            return () => clearTimeout(timeoutId);
        } else {
            setActiveRentalsWarning(null);
        }
    }, [rentData.tc, rentData.mail, showRentModal, checkActiveRentals]);

    const parseCSV = (csvText) => {
        const lines = csvText.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
            throw new Error('CSV dosyası en az bir başlık ve bir veri satırı içermelidir.');
        }

        // Header'ı al ve normalize et
        const headerLine = lines[0].trim();
        const headers = headerLine.split(',').map(h => h.trim().toLowerCase());
        
        // Header mapping: CSV'deki kolon isimlerini backend'deki property'lere map et
        const headerMap = {
            'standno': 'standNo',
            'kitap adi': 'kitapAdi',
            'kitapadi': 'kitapAdi',
            'yazaradi': 'yazarAdi',
            'yayinevi': 'yayinEvi',
            'sirano': 'siraNo'
        };

        const normalizedHeaders = headers.map(h => headerMap[h] || h);
        
        // StandNo, RafNo, KitapAdi, YazarAdi, YayinEvi, SiraNo sırasını kontrol et
        const expectedHeaders = ['standno', 'rafno', 'kitap adi', 'kitapadi', 'yazaradi', 'yayinevi', 'sirano'];
        const hasRafNo = headers.includes('rafno') || headers.length > 5; // CSV'de RafNo header'ı olmayabilir ama veri olabilir

        const books = [];
        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            // CSV parsing - virgülle ayrılmış değerleri al
            const values = [];
            let currentValue = '';
            let inQuotes = false;
            
            for (let j = 0; j < line.length; j++) {
                const char = line[j];
                if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                    values.push(currentValue.trim());
                    currentValue = '';
                } else {
                    currentValue += char;
                }
            }
            values.push(currentValue.trim()); // Son değer

            if (values.length < 5) {
                continue; // Eksik satırları atla
            }

            // CSV formatı: StandNo, RafNo (opsiyonel), Kitap Adi, YazarAdi, YayinEvi, SiraNo
            // Eğer 6 değer varsa, ikinci değer RafNo olabilir
            let standNo, rafNo, kitapAdi, yazarAdi, yayinEvi, siraNo;
            
            if (values.length === 6) {
                // StandNo, RafNo, Kitap Adi, YazarAdi, YayinEvi, SiraNo
                [standNo, rafNo, kitapAdi, yazarAdi, yayinEvi, siraNo] = values;
            } else if (values.length === 5) {
                // StandNo, Kitap Adi, YazarAdi, YayinEvi, SiraNo (RafNo yok)
                [standNo, kitapAdi, yazarAdi, yayinEvi, siraNo] = values;
                rafNo = standNo; // RafNo yoksa StandNo'yu kullan
            } else {
                continue; // Geçersiz format
            }

            books.push({
                standNo: standNo || '',
                rafNo: rafNo || standNo || '',
                kitapAdi: kitapAdi || '',
                yazarAdi: yazarAdi || '',
                yayinEvi: yayinEvi || '',
                siraNo: siraNo || ''
            });
        }

        return books;
    };

    const handleCSVUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.name.endsWith('.csv')) {
            alert('Lütfen CSV dosyası seçin.');
            return;
        }

        setBulkLoading(true);
        setBulkResult(null);

        try {
            const text = await file.text();
            const books = parseCSV(text);

            if (books.length === 0) {
                throw new Error('CSV dosyasından kitap verisi bulunamadı.');
            }

            const response = await bulkCreateBooks(books);
            
            // api.js interceptor'ı response.data'yı direkt döndürüyor, bu yüzden response zaten ServiceResult
            if (response?.isSuccess) {
                setBulkResult(response.data);
                setShowBulkModal(true);
                loadAllBooks(); // Tüm kitapları yeniden yükle
            } else {
                // Hata durumunda bile data varsa göster (kısmi başarı durumu)
                if (response?.data) {
                    setBulkResult(response.data);
                    setShowBulkModal(true);
                    loadAllBooks(); // Tüm kitapları yeniden yükle
                } else {
                    throw new Error(response?.resultMessage || 'Toplu ekleme başarısız.');
                }
            }
        } catch (error) {
            console.error('CSV yükleme hatası:', error);
            alert(error.message || 'CSV dosyası yüklenirken bir hata oluştu.');
        } finally {
            setBulkLoading(false);
            // File input'u temizle
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    if (!canManage) {
        return <div className="p-4">Bu sayfayı görüntüleme yetkiniz yok.</div>;
    }

    const handleHistorySearch = (e) => {
        e.preventDefault();
        setHistoryPagination(prev => ({ ...prev, page: 1 }));
        loadHistory();
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Active':
                return <Badge bg="warning" text="dark">Aktif</Badge>;
            case 'Returned':
                return <Badge bg="success">İade Edildi</Badge>;
            case 'Overdue':
                return <Badge bg="danger">Süresi Doldu</Badge>;
            default:
                return <Badge bg="secondary">{status}</Badge>;
        }
    };

    return (
        <div className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Kütüphane Kitap Yönetimi</h2>
                {activeTab === 'books' && (
                    <div>
                        {isAdmin && (
                            <>
                                <Button variant="outline-primary" className="me-2" onClick={() => fileInputRef.current?.click()}>
                                    {bulkLoading ? 'Yükleniyor...' : 'CSV\'den Toplu Ekle'}
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleCSVUpload}
                                    style={{ display: 'none' }}
                                />
                            </>
                        )}
                        <Button variant="primary" onClick={openAddModal}>Yeni Kitap Ekle</Button>
                    </div>
                )}
            </div>

            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'books')}>
                <Nav variant="tabs" className="mb-3">
                    <Nav.Item>
                        <Nav.Link eventKey="books">Kitaplar</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="history">Kiralama Geçmişi</Nav.Link>
                    </Nav.Item>
                </Nav>

                <Tab.Content>
                    <Tab.Pane eventKey="books">

            <div className="mb-4">
                <InputGroup>
                    <Form.Control
                        placeholder="Kitap Adı, Yazar, Yayınevi, Stand No, Raf No veya Sıra No Ara..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            // Arama yaparken sayfayı 1'e sıfırla (filtreleme useEffect'te otomatik yapılacak)
                            setPagination(prev => ({ ...prev, page: 1 }));
                        }}
                    />
                    {searchTerm && (
                        <Button 
                            variant="outline-danger" 
                            onClick={() => {
                                setSearchTerm('');
                                setPagination(prev => ({ ...prev, page: 1 }));
                            }}
                        >
                            Temizle
                        </Button>
                    )}
                </InputGroup>
                <div className="mt-2 d-flex gap-3 align-items-center">
                    {searchTerm ? (
                        <small className="text-muted">
                            {books.length} kitap bulundu (toplam {pagination.totalCount} sonuçtan)
                        </small>
                    ) : (
                        <>
                            <span
                                className={showOnlyRented ? 'text-muted' : 'text-primary'}
                                onClick={() => {
                                    setShowOnlyRented(false);
                                    setPagination(prev => ({ ...prev, page: 1 })); // İlk sayfaya dön
                                }}
                                title="Tüm kitapları göster"
                                style={{
                                    cursor: 'pointer',
                                    fontWeight: showOnlyRented ? 'normal' : 'bold',
                                    fontSize: '0.875rem',
                                    userSelect: 'none',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    if (!showOnlyRented) {
                                        e.target.style.opacity = '0.8';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.opacity = '1';
                                }}
                            >
                                Tüm Kitaplar ({allBooks.length})
                            </span>
                            <span
                                className={showOnlyRented ? 'text-primary' : 'text-muted'}
                                onClick={() => {
                                    setShowOnlyRented(true);
                                    setPagination(prev => ({ ...prev, page: 1 })); // İlk sayfaya dön
                                }}
                                title="Sadece kiralanmış kitapları göster"
                                style={{
                                    cursor: 'pointer',
                                    fontWeight: showOnlyRented ? 'bold' : 'normal',
                                    fontSize: '0.875rem',
                                    userSelect: 'none',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    if (showOnlyRented) {
                                        e.target.style.opacity = '0.8';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.opacity = '1';
                                }}
                            >
                                Kiradaki Kitaplar ({allBooks.filter(b => b.isRented).length})
                            </span>
                        </>
                    )}
                </div>
            </div>

            <Table striped bordered hover responsive>
                <thead>
                    <tr>
                        <th>Stand No</th>
                        <th>Raf No</th>
                        <th>Sıra No</th>
                        <th>Kitap Adı</th>
                        <th>Yazar</th>
                        <th>Yayınevi</th>
                        <th>Durum</th>
                        <th>İşlemler</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr><td colSpan="8" className="text-center">Yükleniyor...</td></tr>
                    ) : books.length === 0 ? (
                        <tr><td colSpan="8" className="text-center">Kitap bulunamadı.</td></tr>
                    ) : (
                        books.map(book => (
                            <tr key={book.id}>
                                <td>{book.standNo}</td>
                                <td>{book.rafNo}</td>
                                <td>{book.siraNo}</td>
                                <td>{book.kitapAdi}</td>
                                <td>{book.yazarAdi}</td>
                                <td>{book.yayinEvi}</td>
                                <td>
                                    {book.isRented ? (
                                        <Badge bg="warning" text="dark">
                                            Kirada ({book.rentedAdSoyad || book.rentedBy})
                                            <br />
                                            <small>{new Date(book.rentedUntil).toLocaleDateString()}</small>
                                        </Badge>
                                    ) : (
                                        <Badge bg="success">Müsait</Badge>
                                    )}
                                </td>
                                <td>
                                    <Button variant="info" size="sm" className="me-2" onClick={() => openEditModal(book)}>Düzenle</Button>
                                    {book.isRented ? (
                                        <Button 
                                            variant="secondary" 
                                            size="sm" 
                                            className="me-2" 
                                            onClick={() => handleReturn(book.id)}
                                            disabled={returning === book.id}
                                        >
                                            {returning === book.id ? 'İade Alınıyor...' : 'İade Al'}
                                        </Button>
                                    ) : (
                                        <Button variant="success" size="sm" className="me-2" onClick={() => openRentModal(book)}>Kirala</Button>
                                    )}
                                    {isAdmin && (
                                        <Button variant="danger" size="sm" onClick={() => handleDelete(book.id)}>Sil</Button>
                                    )}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>

            <div className="d-flex justify-content-center align-items-center gap-2 my-3">
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                >
                    Önceki
                </Button>
                <span className="mx-2">Sayfa</span>
                <Form.Control
                    type="number"
                    min="1"
                    max={Math.ceil(pagination.totalCount / pagination.pageSize) || 1}
                    value={pagination.page}
                    onChange={(e) => {
                        const page = parseInt(e.target.value) || 1;
                        const maxPage = Math.ceil(pagination.totalCount / pagination.pageSize) || 1;
                        const validPage = Math.max(1, Math.min(page, maxPage));
                        setPagination(prev => ({ ...prev, page: validPage }));
                    }}
                    onBlur={() => {
                        const maxPage = Math.ceil(pagination.totalCount / pagination.pageSize) || 1;
                        if (pagination.page > maxPage) {
                            setPagination(prev => ({ ...prev, page: maxPage }));
                        }
                        // useEffect otomatik olarak filtreleme ve sayfalama yapacak
                    }}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            e.target.blur();
                        }
                    }}
                    style={{ width: '60px', textAlign: 'center' }}
                />
                <span className="mx-1">/</span>
                <span>{Math.ceil(pagination.totalCount / pagination.pageSize) || 1}</span>
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page * pagination.pageSize >= pagination.totalCount}
                >
                    Sonraki
                </Button>
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => {
                        const lastPage = Math.ceil(pagination.totalCount / pagination.pageSize) || 1;
                        setPagination(prev => ({ ...prev, page: lastPage }));
                    }}
                    disabled={pagination.page * pagination.pageSize >= pagination.totalCount}
                >
                    Son Sayfa
                </Button>
            </div>

            {/* Add/Edit Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedBook ? 'Kitap Düzenle' : 'Yeni Kitap Ekle'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddEdit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Stand No</Form.Label>
                            <Form.Control required value={formData.standNo} onChange={e => setFormData({ ...formData, standNo: e.target.value })} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Raf No</Form.Label>
                            <Form.Control required value={formData.rafNo} onChange={e => setFormData({ ...formData, rafNo: e.target.value })} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Sıra No</Form.Label>
                            <Form.Control type="number" required value={formData.siraNo} onChange={e => setFormData({ ...formData, siraNo: parseInt(e.target.value) })} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Kitap Adı</Form.Label>
                            <Form.Control required value={formData.kitapAdi} onChange={e => setFormData({ ...formData, kitapAdi: e.target.value })} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Yazar Adı</Form.Label>
                            <Form.Control required value={formData.yazarAdi} onChange={e => setFormData({ ...formData, yazarAdi: e.target.value })} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Yayınevi</Form.Label>
                            <Form.Control required value={formData.yayinEvi} onChange={e => setFormData({ ...formData, yayinEvi: e.target.value })} />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowAddModal(false)}>İptal</Button>
                        <Button variant="primary" type="submit">Kaydet</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Rent Modal */}
            <Modal show={showRentModal} onHide={() => {
                if (!renting) {
                    setShowRentModal(false);
                    setRenting(false);
                }
            }}>
                <Modal.Header closeButton={!renting}>
                    <Modal.Title>Kitap Kirala: {selectedBook?.kitapAdi}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleRent}>
                    <Modal.Body>
                        {activeRentalsWarning && (
                            <Alert variant="warning" className="mb-3">
                                <Alert.Heading>Uyarı: Aktif Kiralama Mevcut</Alert.Heading>
                                <p>
                                    Bu kullanıcının zaten <strong>{activeRentalsWarning.count}</strong> aktif kitap kiralaması bulunmaktadır:
                                </p>
                                <ul className="mb-0">
                                    {activeRentalsWarning.bookNames.split(', ').map((bookName, idx) => (
                                        <li key={idx}>{bookName}</li>
                                    ))}
                                </ul>
                            </Alert>
                        )}
                        {checkingActiveRentals && (
                            <Alert variant="info" className="mb-3">
                                Aktif kiralama kontrol ediliyor...
                            </Alert>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>Ad Soyad <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                                required 
                                value={rentData.adSoyad} 
                                onChange={e => setRentData({ ...rentData, adSoyad: e.target.value })} 
                                placeholder="Ad Soyad"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>TC Kimlik No</Form.Label>
                            <Form.Control 
                                value={rentData.tc} 
                                onChange={e => setRentData({ ...rentData, tc: e.target.value })} 
                                placeholder="TC Kimlik No"
                                maxLength={11}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Telefon</Form.Label>
                            <Form.Control 
                                value={rentData.telefon} 
                                onChange={e => {
                                    // Sadece rakam girişine izin ver
                                    const value = e.target.value.replace(/\D/g, '');
                                    // Maksimum 11 karakter
                                    if (value.length <= 11) {
                                        setRentData({ ...rentData, telefon: value });
                                    }
                                }} 
                                placeholder="Telefon (sadece rakam, max 11)"
                                maxLength={11}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>E-posta</Form.Label>
                            <Form.Control 
                                type="email"
                                value={rentData.mail} 
                                onChange={e => setRentData({ ...rentData, mail: e.target.value })} 
                                placeholder="E-posta adresi"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Süre (Gün) <span className="text-danger">*</span></Form.Label>
                            <Form.Control 
                                type="number" 
                                required 
                                min="1"
                                value={rentData.durationDays} 
                                onChange={e => setRentData({ ...rentData, durationDays: parseInt(e.target.value) || 15 })} 
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowRentModal(false)} disabled={renting}>İptal</Button>
                        <Button variant="success" type="submit" disabled={renting}>
                            {renting ? 'Kiralanıyor...' : 'Kirala'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
                    </Tab.Pane>

                    <Tab.Pane eventKey="history">
                        <div className="mb-4">
                            <Form onSubmit={handleHistorySearch}>
                                <div className="row g-2">
                                    <div className="col-md-6">
                                        <InputGroup>
                                            <Form.Control
                                                placeholder="Ad Soyad, TC, Telefon, Mail veya Kitap Adı Ara..."
                                                value={historySearchTerm}
                                                onChange={(e) => setHistorySearchTerm(e.target.value)}
                                            />
                                            <Button variant="outline-secondary" type="submit">Ara</Button>
                                        </InputGroup>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Select
                                            value={historyStatus}
                                            onChange={(e) => {
                                                setHistoryStatus(e.target.value);
                                                setHistoryPagination(prev => ({ ...prev, page: 1 }));
                                            }}
                                        >
                                            <option value="">Tüm Durumlar</option>
                                            <option value="Active">Aktif</option>
                                            <option value="Returned">İade Edildi</option>
                                            <option value="Overdue">Süresi Doldu</option>
                                        </Form.Select>
                                    </div>
                                </div>
                            </Form>
                        </div>

                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>Kitap Adı</th>
                                    <th>Yazar</th>
                                    <th>Ad Soyad</th>
                                    <th>TC</th>
                                    <th>Telefon</th>
                                    <th>Mail</th>
                                    <th>Kiralama Tarihi</th>
                                    <th>Beklenen İade</th>
                                    <th>İade Tarihi</th>
                                    <th>Süre (Gün)</th>
                                    <th>Durum</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historyLoading ? (
                                    <tr><td colSpan="11" className="text-center">Yükleniyor...</td></tr>
                                ) : history.length === 0 ? (
                                    <tr><td colSpan="11" className="text-center">Kiralama geçmişi bulunamadı.</td></tr>
                                ) : (
                                    history.map(item => (
                                        <tr key={item.id}>
                                            <td>{item.bookName || '-'}</td>
                                            <td>{item.bookAuthor || '-'}</td>
                                            <td>{item.adSoyad}</td>
                                            <td>{item.tc || '-'}</td>
                                            <td>{item.telefon || '-'}</td>
                                            <td>{item.mail || '-'}</td>
                                            <td>{formatDate(item.rentedDate)}</td>
                                            <td>{formatDate(item.expectedReturnDate)}</td>
                                            <td>{formatDate(item.returnDate)}</td>
                                            <td>{item.durationDays}</td>
                                            <td>{getStatusBadge(item.status)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>

                        <div className="d-flex justify-content-center align-items-center gap-2 my-3">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => setHistoryPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                disabled={historyPagination.page === 1}
                            >
                                Önceki
                            </Button>
                            <span className="mx-2">Sayfa</span>
                            <Form.Control
                                type="number"
                                min="1"
                                max={Math.ceil(historyPagination.totalCount / historyPagination.pageSize) || 1}
                                value={historyPagination.page}
                                onChange={(e) => {
                                    const page = parseInt(e.target.value) || 1;
                                    const maxPage = Math.ceil(historyPagination.totalCount / historyPagination.pageSize) || 1;
                                    const validPage = Math.max(1, Math.min(page, maxPage));
                                    setHistoryPagination(prev => ({ ...prev, page: validPage }));
                                }}
                                onBlur={() => {
                                    const maxPage = Math.ceil(historyPagination.totalCount / historyPagination.pageSize) || 1;
                                    if (historyPagination.page > maxPage) {
                                        setHistoryPagination(prev => ({ ...prev, page: maxPage }));
                                    }
                                    loadHistory();
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.target.blur();
                                    }
                                }}
                                style={{ width: '60px', textAlign: 'center' }}
                            />
                            <span className="mx-1">/</span>
                            <span>{Math.ceil(historyPagination.totalCount / historyPagination.pageSize) || 1}</span>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => setHistoryPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                disabled={historyPagination.page * historyPagination.pageSize >= historyPagination.totalCount}
                            >
                                Sonraki
                            </Button>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => {
                                    const lastPage = Math.ceil(historyPagination.totalCount / historyPagination.pageSize) || 1;
                                    setHistoryPagination(prev => ({ ...prev, page: lastPage }));
                                }}
                                disabled={historyPagination.page * historyPagination.pageSize >= historyPagination.totalCount}
                            >
                                Son Sayfa
                            </Button>
                        </div>
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>

            {/* Bulk Upload Result Modal */}
            <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Toplu Ekleme Sonucu</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {bulkResult && (
                        <>
                            <Alert variant={bulkResult.failedCount > 0 ? 'warning' : 'success'}>
                                <strong>Toplam:</strong> {bulkResult.totalCount} kitap<br />
                                <strong>Başarılı:</strong> {bulkResult.successCount} kitap<br />
                                {bulkResult.failedCount > 0 && (
                                    <>
                                        <strong>Başarısız:</strong> {bulkResult.failedCount} kitap
                                    </>
                                )}
                            </Alert>
                            {bulkResult.errors && bulkResult.errors.length > 0 && (
                                <div className="mt-3">
                                    <strong>Hatalar:</strong>
                                    <ul className="mt-2">
                                        {bulkResult.errors.map((error, index) => (
                                            <li key={index} className="text-danger small">{error}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowBulkModal(false)}>Tamam</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default BookManagement;
